import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 53. VERIFICATION
// Plusieurs niveaux de vérification, du moins au plus engagé :
//
//   Unverified · Basic Verified · Business Verified · Professional Verified
//
// ⚠️ Une vérification ne doit JAMAIS être présentée comme une garantie de
// qualité. Elle atteste uniquement que des critères définis par Zifek ont
// été satisfaits (identité, entreprise, profession).
// ─────────────────────────────────────────────────────────────

export type VerificationLevel = 'unverified' | 'basic' | 'business' | 'professional';

export const VERIFICATION_LEVELS: {
  key: VerificationLevel;
  label: string;
  icon: string;
  className: string;
  desc: string;
  order: number;
}[] = [
  { key: 'unverified', label: 'Non vérifié', icon: 'ri-question-line', className: 'bg-background-100 text-foreground-600', desc: 'Aucune vérification réalisée.', order: 0 },
  { key: 'basic', label: 'Basic Verified', icon: 'ri-shield-check-line', className: 'bg-secondary-100 text-secondary-900', desc: 'Coordonnées et identité de base confirmées.', order: 1 },
  { key: 'business', label: 'Business Verified', icon: 'ri-building-2-line', className: 'bg-primary-100 text-primary-900', desc: 'Entreprise enregistrée et documents validés.', order: 2 },
  { key: 'professional', label: 'Professional Verified', icon: 'ri-award-line', className: 'bg-accent-100 text-accent-900', desc: 'Diplômes et références professionnelles validés.', order: 3 },
];

export function verificationLevelMeta(key: string | null | undefined) {
  return VERIFICATION_LEVELS.find((l) => l.key === key) || VERIFICATION_LEVELS[0];
}

/** Avertissement obligatoire : « Vérifié » ≠ garantie de qualité. */
export const VERIFICATION_DISCLAIMER =
  'Une vérification atteste uniquement que des critères définis par Zifek ont été satisfaits. Elle ne constitue en aucun cas une garantie de qualité du service rendu.';

export interface VerificationCriterion {
  key: string;
  label: string;
  done: boolean;
}

/** Critères requis par niveau (liste de contrôle). */
export const LEVEL_CRITERIA: Record<VerificationLevel, { key: string; label: string }[]> = {
  unverified: [],
  basic: [
    { key: 'phone_confirmed', label: 'Téléphone confirmé' },
    { key: 'email_confirmed', label: 'Email confirmé' },
    { key: 'identity', label: 'Pièce d’identité fournie' },
  ],
  business: [
    { key: 'registration', label: 'Registre du commerce / immatriculation' },
    { key: 'tax_id', label: 'Numéro fiscal' },
    { key: 'address_proof', label: 'Justificatif d’adresse' },
    { key: 'legal_representative', label: 'Représentant légal identifié' },
  ],
  professional: [
    { key: 'diplomas', label: 'Diplômes / certifications' },
    { key: 'experience', label: 'Expérience justifiée' },
    { key: 'references', label: 'Références vérifiables' },
    { key: 'insurance', label: 'Assurance professionnelle (si applicable)' },
  ],
};

export interface CoreVerification {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  entity_type: string | null;
  entity_id: string | null;
  level: string | null;
  status: string | null;
  criteria: VerificationCriterion[] | null;
  documents: unknown[] | null;
  notes: string | null;
  reviewed_by: number | null;
  requested_at: string | null;
  verified_at: string | null;
  created_at: string | null;
}

export function verificationStatusMeta(status: string | null | undefined): { label: string; className: string } {
  switch (status) {
    case 'pending': return { label: 'En cours d’examen', className: 'bg-accent-100 text-accent-900' };
    case 'approved': return { label: 'Validée', className: 'bg-primary-100 text-primary-900' };
    case 'rejected': return { label: 'Refusée', className: 'bg-red-100 text-red-800' };
    default: return { label: 'Non demandée', className: 'bg-background-100 text-foreground-500' };
  }
}

export async function listVerifications(organizationId: number): Promise<CoreVerification[]> {
  const { data, error } = await supabase
    .from('core_verifications')
    .select('*')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as CoreVerification[];
}

export async function getVerification(
  organizationId: number,
  entityType: string,
  entityId: string | number,
): Promise<CoreVerification | null> {
  const { data, error } = await supabase
    .from('core_verifications')
    .select('*')
    .eq('organization_id', organizationId)
    .eq('entity_type', entityType)
    .eq('entity_id', String(entityId))
    .maybeSingle();
  if (error) throw error;
  return (data as CoreVerification) || null;
}

/** Liste de contrôle du niveau avec l'état de chaque critère. */
export function criteriaFor(level: VerificationLevel, done?: string[]): VerificationCriterion[] {
  const doneSet = new Set(done || []);
  return LEVEL_CRITERIA[level].map((c) => ({ ...c, done: doneSet.has(c.key) }));
}

export interface RequestVerificationInput {
  organizationId: number;
  tenantId?: number | null;
  entityType?: string;
  entityId?: string | number;
  level: VerificationLevel;
  criteria?: VerificationCriterion[];
  documents?: unknown[];
  notes?: string | null;
}

/** Dépose (ou met à jour) une demande de vérification au niveau visé. */
export async function requestVerification(input: RequestVerificationInput): Promise<number | null> {
  const entityType = input.entityType || 'business';
  const entityId = input.entityId != null ? String(input.entityId) : String(input.organizationId);
  const existing = await getVerification(input.organizationId, entityType, entityId);

  const criteria = input.criteria || criteriaFor(input.level);

  const payload = {
    tenant_id: input.tenantId ?? null,
    organization_id: input.organizationId,
    entity_type: entityType,
    entity_id: entityId,
    level: input.level,
    status: 'pending',
    criteria,
    documents: input.documents || [],
    notes: input.notes || null,
    requested_at: new Date().toISOString(),
  };

  if (existing?.id) {
    const { error } = await supabase.from('core_verifications').update(payload).eq('id', existing.id);
    if (error) throw error;
    return existing.id;
  }
  const { data, error } = await supabase.from('core_verifications').insert(payload).select('id').maybeSingle();
  if (error) throw error;
  return (data?.id as number) ?? null;
}

export interface ReviewVerificationInput {
  verificationId: number;
  decision: 'approved' | 'rejected';
  level?: VerificationLevel;
  notes?: string | null;
  reviewerUserId?: number | null;
  /** Répercute le niveau sur le profil public / professionnel. */
  organizationId?: number | null;
  entityType?: string;
  entityId?: string | number;
}

/** Valide ou refuse une demande (usage back-office / superadmin). */
export async function reviewVerification(input: ReviewVerificationInput): Promise<void> {
  const payload: Record<string, unknown> = {
    status: input.decision,
    notes: input.notes || null,
    reviewed_by: input.reviewerUserId ?? null,
    verified_at: input.decision === 'approved' ? new Date().toISOString() : null,
  };
  if (input.decision === 'approved' && input.level) payload.level = input.level;

  const { error } = await supabase.from('core_verifications').update(payload).eq('id', input.verificationId);
  if (error) throw error;

  // Répercussion du niveau vérifié sur le profil public de l'entreprise.
  if (input.decision === 'approved' && input.level && input.organizationId != null) {
    try {
      await supabase
        .from('core_business_profiles')
        .update({ verification_level: input.level, updated_at: new Date().toISOString() })
        .eq('organization_id', input.organizationId);
    } catch {
      /* best-effort */
    }
  }
  if (input.decision === 'approved' && input.level && input.entityType === 'professional' && input.entityId != null) {
    try {
      await supabase.from('core_professionals').update({ verification_status: input.level }).eq('id', input.entityId);
    } catch {
      /* best-effort */
    }
  }
}

export interface VerificationStats {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  byLevel: Record<string, number>;
}

export async function fetchVerificationStats(organizationId: number): Promise<VerificationStats> {
  const rows = await listVerifications(organizationId);
  const byLevel: Record<string, number> = {};
  let pending = 0;
  let approved = 0;
  let rejected = 0;
  rows.forEach((r) => {
    const lvl = r.level || 'unverified';
    byLevel[lvl] = (byLevel[lvl] || 0) + 1;
    if (r.status === 'pending') pending += 1;
    else if (r.status === 'approved') approved += 1;
    else if (r.status === 'rejected') rejected += 1;
  });
  return { total: rows.length, pending, approved, rejected, byLevel };
}